import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { addMessage, bonoBotState, setChatInput } from 'store/bonoBot/bonoBot.slice';
import { ExamplesType, ExamplesDataType, MessageActionStatusType } from 'store/bonoBot/types';
import { CharityType } from 'store/causes/types';
import { useAppDispatch, useAppSelector } from 'store/hooks';

type SocketInstanceContextType = {
   sendMessageToServer: (message: string, storedThreadId: string | null, cb?: () => void) => void;
   sendMessageToServerAndChat: (message: string, storedThreadId: string | null, aiMessage?: string) => void;
   isWaitingForResponse: boolean;
   closeSocket: () => void;
};

const initialState: SocketInstanceContextType = {
   sendMessageToServer: () => undefined,
   sendMessageToServerAndChat: () => undefined,
   isWaitingForResponse: false,
   closeSocket: () => undefined,
};

export const SocketInstanceContext = createContext<SocketInstanceContextType>(initialState);

const SocketInstanceContextProvider = ({ children }: { children: React.ReactNode }) => {
   const dispatch = useAppDispatch();
   const { selectedCharities } = useAppSelector(bonoBotState);

   const [socketInstance, setSocketInstance] = useState<Socket | null>(null);
   const [isWaitingForResponse, setIsWaitingForResponse] = useState<boolean>(initialState.isWaitingForResponse);

   const dispatchBotMessage = (data: {
      message: {
         text: string;
         examples?: ExamplesDataType;
         examplesType?: ExamplesType;
         status?: MessageActionStatusType;
         optionalCharities?: CharityType[];
         causes?: number[];
         charities?: number[];
      };
      threadId: string;
   }) => {
      console.log('============================================');
      console.log('BotMessage', data.message, { selectedCharities });
      console.log('============================================');

      const {
         message: { text, examples, examplesType, optionalCharities, status, causes, charities },
         threadId,
      } = data;

      if (examplesType === 'charity' && examples?.charity?.id && selectedCharities.length > 0) {
         if (selectedCharities.find((ch) => ch.id === examples?.charity?.id)) {
            console.log({ examplesType, examplesCharityId: examples?.charity?.id, selectedCharities });
            sendMessageToServer('no', threadId);
            return;
         }
      }

      setIsWaitingForResponse(false);

      if (text || examplesType === 'amount') {
         dispatch(
            addMessage({
               role: 'bot',
               text,
               examplesType,
               examples,
               status,
               optionalCharities,
               causes,
               charities,
               threadId,
            })
         );
      }
   };

   const closeSocket = () => {
      socketInstance?.disconnect();
      setSocketInstance(null);
   };

   const initSocket = () => {
      console.log('socket is initialising');

      if (process.env.REACT_APP_API_URL && !socketInstance) {
         const socket = io(`${process.env.REACT_APP_API_URL}/chat`);
         socket.on('connect', () => {
            console.log('Connected to socket server');
            socket.on('disconnect', (reason) => {
               if (reason === 'io server disconnect') {
                  // the disconnection was initiated by the server, you need to manually reconnect
                  socket.connect();
               }
            });
         });
         socket.on('response', dispatchBotMessage);
         setSocketInstance(socket);
      }
   };

   const sendMessageToServerAndChat = (message: string, storedThreadId: string | null, aiMessage?: string) => {
      if (!socketInstance) {
         initSocket();
      }

      console.log('socket is active', socketInstance?.active);
      if (socketInstance?.active) {
         const enrichedMessage = aiMessage ? `${message}, ${aiMessage}` : message;
         socketInstance.emit('user-message', { threadId: storedThreadId, message: enrichedMessage });
         dispatch(addMessage({ role: 'user', text: message }));
         dispatch(setChatInput(''));
         setIsWaitingForResponse(true);
      } else console.log("message wasn't sent");
   };

   const sendMessageToServer = (message: string, storedThreadId: string | null) => {
      if (!socketInstance) {
         initSocket();
      }

      console.log('socket is active', socketInstance?.active);
      if (socketInstance?.active) {
         socketInstance.emit('user-message', { threadId: storedThreadId, message });
         setIsWaitingForResponse(true);
      } else console.log("message wasn't sent");
   };

   useEffect(() => {
      if (!socketInstance) {
         initSocket();
      }
      return () => {
         socketInstance?.off('connect');
         socketInstance?.off('response', dispatchBotMessage);
         socketInstance?.disconnect();
         setSocketInstance(null);
      };
   }, []);

   useEffect(() => {
      if (socketInstance) {
         socketInstance.off('response');
         socketInstance.on('response', dispatchBotMessage);
      }
   }, [selectedCharities]);

   useEffect(() => {
      let timer: NodeJS.Timeout;
      if (isWaitingForResponse) {
         timer = setTimeout(() => {
            setIsWaitingForResponse(false);
         }, 30000);
      }
      return () => timer && clearTimeout(timer);
   }, [isWaitingForResponse]);

   const contextValue: SocketInstanceContextType = useMemo(
      () => ({
         sendMessageToServerAndChat,
         sendMessageToServer,
         isWaitingForResponse,
         closeSocket,
      }),
      [sendMessageToServerAndChat, sendMessageToServer, isWaitingForResponse]
   );

   return <SocketInstanceContext.Provider value={contextValue}>{children}</SocketInstanceContext.Provider>;
};

export default SocketInstanceContextProvider;

export const useSocketInstanceContext = () => {
   const context = useContext(SocketInstanceContext);

   if (!context) {
      throw new Error('useSocketInstanceContext must be used within a SocketInstanceContextProvider');
   }

   return context;
};
